import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="ai-correction"
export default class extends Controller {
  private isContentValid = false;
  private isAiRunning = false;
  private isContentSameAsIA = false;
  private isContentDifAsPublish = false;
  private correctText = "";
  private controller = new AbortController();

  static values = {
    answer: String,
    aiQuotaReached: Boolean,
    limit: Number,
  };

  static targets = [
    "btnCorrection",
    "editZone",
    "aiZone",
    "btnZoneAi",
    "spanText",
    "btnClose",
    "btnSave",
    "btnUnpublish",
    "btnPublish",
    "btnCancel",
    "tooltipPublish",
    "tooltipModif",
  ];

  declare btnCorrectionTarget: HTMLElement;
  declare editZoneTarget: HTMLTextAreaElement;
  declare aiZoneTarget: HTMLElement;
  declare btnZoneAiTarget: HTMLElement;
  declare spanTextTarget: HTMLElement;
  declare btnCloseTarget: HTMLElement;
  declare btnSaveTarget: HTMLInputElement;
  declare btnUnpublishTarget: HTMLInputElement;
  declare btnPublishTarget: HTMLInputElement;
  declare btnCancelTarget: HTMLElement;
  declare tooltipPublishTarget: HTMLElement;
  declare tooltipModifTarget: HTMLElement;

  declare hasBtnUnpublishTarget: boolean;

  declare answerValue: string;
  declare aiQuotaReachedValue: boolean;
  declare limitValue: number;

  connect() {
    this.toggleButtons();
    window.addEventListener("ai-quota-reached", this.handleQuotaReached);
  }

  disconnect() {
    window.removeEventListener("ai-quota-reached", this.handleQuotaReached);
  }

  handleQuotaReached = () => {
    this.aiQuotaReachedValue = true;
    this.toggleButtons();
  };

  async showAI() {
    if (!this.isContentValid || this.isContentSameAsIA || this.aiQuotaReachedValue) return;

    this.controller = new AbortController();
    const signal = this.controller.signal;

    this.btnCorrectionTarget.classList.add("hidden");
    this.aiZoneTarget.classList.remove("hidden");
    this.btnCancelTarget.classList.remove("hidden");
    this.editZoneTarget.disabled = true;
    this.isAiRunning = true;

    this.toggleButtons();

    const content = (this.editZoneTarget.value || "").trim();
    try {
      this.aiZoneTarget.innerHTML = `
      <div class="m-5 grid grid-cols-3 gap-4 animate-pulse">
        <div class="h-3 col-span-2 rounded bg-gray-200"></div>
        <div class="h-3 rounded col-span-1 bg-gray-200"></div>
        <div class="h-3 rounded col-span-3 bg-gray-200"></div>
        <div class="h-3 rounded col-span-1 bg-gray-200"></div>
        <div class="h-3 rounded col-span-2 bg-gray-200"></div>
      </div>
    `;
      const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
      if (!meta) {
        throw new Error("CSRF token not found");
      }
      const token = meta.content;
      const response = await fetch("/events/ai_correction", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": token,
        },
        body: JSON.stringify({ text: content }),
        signal,
      });

      if (!response.ok) {
        let message = "Erreur lors de la correction par IA.";

        try {
          const errorData = await response.json();
          message = errorData.error || message;
          this.aiQuotaReachedValue = errorData.ai_requests_count >= this.limitValue;
          if (this.aiQuotaReachedValue) {
            window.dispatchEvent(new CustomEvent("ai-quota-reached"));
          }
        } catch (_) {}

        throw new Error(message);
      }

      const data = await response.json();

      this.editZoneTarget.disabled = false;
      this.btnCancelTarget.classList.add("hidden");

      this.aiQuotaReachedValue = data.ai_requests_count >= this.limitValue;
      if (this.aiQuotaReachedValue) {
        window.dispatchEvent(new CustomEvent("ai-quota-reached"));
      }

      const originalText = this.editZoneTarget.value;
      const modifications = data.modifications || [];
      this.correctText = data.texte_corrige;

      let result = "";
      let lastIndex = 0;
      let searchCursor = 0;

      modifications.forEach((modification: any) => {
        const original = modification.original;
        const corrected = modification.corrige;
        const reason = modification.raison;
        let start = this.correctText.indexOf(corrected, searchCursor);
        if (start < 0) return;
        while (
          this.correctText.slice(start, start + (corrected.length > 0 ? corrected.length : 1)) ===
            originalText.slice(start, start + (corrected.length > 0 ? corrected.length : 1)) &&
          originalText.indexOf(original, searchCursor) == originalText.indexOf(corrected, searchCursor) &&
          original == corrected
        ) {
          if (start < 0) return;
          searchCursor += corrected.length;
          start = this.correctText.indexOf(corrected, searchCursor);
        }
        const end = start + corrected.length;
        result += this.correctText.slice(lastIndex, start);
        result += `<span class="parent-tooltip relative inline-block" data-original="${original}" data-corrected="${corrected}" data-reason="${reason}">
                    <span class="underline underline-offset-4 decoration-dashed decoration-2 decoration-gray-400 text-primary">${corrected}</span>
                  </span>`;
        lastIndex = end;
        searchCursor = end;
      });
      result += this.correctText.slice(lastIndex);

      if (modifications.length <= 0 || this.correctText == originalText) {
        this.aiZoneTarget.innerHTML = "<p class='text-primary'>Aucune erreur trouvée !</p>";
        this.btnCloseTarget.classList.remove("hidden");
      } else {
        this.aiZoneTarget.innerHTML = result;
        this.btnZoneAiTarget.classList.remove("hidden");
      }

      const textsModif = document.querySelectorAll(".parent-tooltip");

      textsModif.forEach((element) => {
        element.addEventListener("mouseover", () => {
          this.showTooltip(element);
        });

        element.addEventListener("mouseleave", () => {
          this.tooltipModifTarget.classList.add("hidden");
        });
      });
    } catch (e: any) {
      if (e?.name === "AbortError") {
        return;
      }
      console.error(e);
      this.btnCancelTarget.classList.add("hidden");
      this.btnCloseTarget.classList.remove("hidden");
      this.aiZoneTarget.innerHTML = `<div class='flex flex-row gap-2 text-red-500'><p>${e.message}</p></div>`;
    }
  }

  cancelAi() {
    this.controller.abort();
    this.btnCancelTarget.classList.add("hidden");
    this.aiZoneTarget.classList.add("hidden");
    this.btnCorrectionTarget.classList.remove("hidden");
    this.btnCloseTarget.classList.add("hidden");

    this.editZoneTarget.disabled = false;
    this.isAiRunning = false;

    if (this.aiQuotaReachedValue) {
      window.dispatchEvent(new CustomEvent("ai-quota-reached"));
    }

    this.editZoneTarget.focus();
    this.toggleButtons();
  }

  closeAi() {
    this.btnCloseTarget.classList.add("hidden");
    this.btnCorrectionTarget.classList.remove("hidden");
    this.btnZoneAiTarget.classList.add("hidden");
    this.aiZoneTarget.classList.add("hidden");
    this.btnSaveTarget.classList.remove("hidden");

    this.isAiRunning = false;

    this.editZoneTarget.focus();
    this.toggleButtons();
  }

  acceptAi() {
    this.editZoneTarget.value = this.correctText.replace(/<[^>]+>/g, " ");
    this.btnCorrectionTarget.classList.remove("hidden");
    this.btnZoneAiTarget.classList.add("hidden");
    this.aiZoneTarget.classList.add("hidden");
    this.btnSaveTarget.classList.remove("hidden");

    this.isAiRunning = false;

    this.editZoneTarget.focus();
    this.toggleButtons();
  }

  toggleButtons() {
    const content = this.editZoneTarget.value.trim();
    this.isContentValid = content.length >= 1;

    this.isContentSameAsIA = this.normalize(this.editZoneTarget.value) === this.normalize(this.correctText || "");

    this.isContentDifAsPublish = content != this.answerValue;

    this.setButtonsState(this.isContentValid, this.isAiRunning, this.isContentDifAsPublish);
    this.setCorrectionButtonState(this.isContentValid, this.isContentSameAsIA);
  }

  setCorrectionButtonState(contentValid: boolean, contentSame: boolean) {
    const el = this.btnCorrectionTarget;
    const enabled = contentValid && !contentSame && !this.aiQuotaReachedValue ? true : false;

    const active = [
      "border-primary-light",
      "bg-white",
      "text-primary",
      "hover:bg-gradient-to-r",
      "hover:from-primary-darker",
      "hover:to-primary-light",
      "hover:text-white",
      "hover:border-none",
      "cursor-pointer",
      "hover:py-[5px]",
      "hover:px-[9px]",
    ];
    const inactive = ["border-gray-500", "text-gray-500", "bg-gray-200", "cursor-not-allowed"];

    el.classList.remove(...active, ...inactive);
    el.classList.add(...(enabled ? active : inactive));

    if (this.aiQuotaReachedValue) {
      this.spanTextTarget.classList.add("group-hover:block");
      this.spanTextTarget.innerHTML = "<p>Vous avez atteint votre quota<br>quotidien d'utilisation de l'IA.</p>";
    } else if (!contentValid) {
      this.spanTextTarget.classList.add("group-hover:block");
      this.spanTextTarget.innerHTML = "<p>Veuillez saisir une réponse</p>";
    } else if (contentSame) {
      this.spanTextTarget.classList.add("group-hover:block");
      this.spanTextTarget.innerHTML =
        "<p>Votre texte a déjà été corrigé par l'IA.<br>Pour réutiliser cette fonctionnalité,<br>veuillez effectuer des modifications.</p>";
    } else {
      this.spanTextTarget.innerHTML = "";
      this.spanTextTarget.classList.remove("group-hover:block");
    }
  }

  setButtonsState(contentValid: boolean, aiRunning: boolean, contentDifAsPublish: boolean) {
    const inactive = ["border-gray-500", "text-gray-500", "bg-gray-200", "cursor-not-allowed"];

    this.btnSaveTarget.classList.remove(...this.getActiveState("orange-600"), ...inactive);
    if (this.hasBtnUnpublishTarget) {
      this.btnUnpublishTarget.classList.remove(...this.getActiveState("red-600"), ...inactive);
    }
    this.btnPublishTarget.classList.remove(...this.getActiveState("green-600"), ...inactive);

    this.btnSaveTarget.classList.add(...(aiRunning ? inactive : this.getActiveState("orange-600")));
    this.btnSaveTarget.disabled = aiRunning;

    if (this.hasBtnUnpublishTarget) {
      this.btnUnpublishTarget.classList.add(...(aiRunning ? inactive : this.getActiveState("red-600")));
      this.btnUnpublishTarget.disabled = aiRunning;
    }

    this.btnPublishTarget.classList.add(
      ...(!aiRunning && contentDifAsPublish ? this.getActiveState("green-600") : inactive),
    );
    this.btnPublishTarget.disabled = aiRunning || !contentDifAsPublish;

    if (this.isAiRunning) {
      this.tooltipPublishTarget.classList.add("group-hover:block");
      this.tooltipPublishTarget.innerHTML = "<p>Veuillez valider ou refuser<br>la correction par IA</p>";
    } else if (!contentValid) {
      this.tooltipPublishTarget.classList.add("group-hover:block");
      this.tooltipPublishTarget.innerHTML = "<p>Veuillez saisir une réponse</p>";
    } else if (!contentDifAsPublish) {
      this.tooltipPublishTarget.classList.add("group-hover:block");
      this.tooltipPublishTarget.innerHTML = "<p>Veuillez saisir une réponse différente<br>de celle déjà publiée</p>";
    } else {
      this.tooltipPublishTarget.innerHTML = "";
      this.tooltipPublishTarget.classList.remove("group-hover:block");
    }
  }

  getActiveState(color: string) {
    return [`border-${color}`, `text-${color}`, `bg-white`, `hover:bg-${color}`, "hover:text-white", "cursor-pointer"];
  }

  showTooltip(element: Element) {
    const htmlElement = element as HTMLElement;

    const original = htmlElement.dataset.original || "";
    const corrected = htmlElement.dataset.corrected || "";
    const reason = htmlElement.dataset.reason || "";

    const rect = htmlElement.getBoundingClientRect();

    this.tooltipModifTarget.innerHTML = `<span class="line-through">${original}</span> 🠒 ${corrected}</span>
                                        <span class="block border-t my-2"></span>
                                        <span class="block text-primary">${reason}</span>`;

    this.tooltipModifTarget.style.left = `${rect.left}px`;
    this.tooltipModifTarget.style.top = `${rect.bottom + 3}px`;

    this.tooltipModifTarget.classList.remove("hidden");
  }

  normalize(text: string): string {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;

    return textarea.value
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, "")
      .trim();
  }
}
